import {GitHubAvatar} from '@github-ui/github-avatar'
import {HOTKEYS} from '@github-ui/item-picker-base/constants/hotkeys'
import {LABELS} from '@github-ui/item-picker-base/constants/labels'
import {SharedPicker} from '@github-ui/item-picker-base/SharedPicker'
import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {CopilotIcon, PeopleIcon} from '@primer/octicons-react'

import type {AssigneePickerBaseAssignee} from './AssigneePickerBase'
import styles from './CompressedAssigneeAnchor.module.css'

export type AssigneeObject = Pick<AssigneePickerBaseAssignee, 'login' | 'avatarUrl' | 'isCopilot'>

export type CompressedAssigneeAnchorProps = {
  assignees: AssigneeObject[]
  anchorProps?: React.HTMLAttributes<HTMLElement>
  displayHotkey: boolean
  MAX_DISPLAYED_ASSIGNEES?: number
  readonly?: boolean
  ref?: React.Ref<HTMLButtonElement>
}

export const CompressedAssigneeAnchor = ({
  ref,
  assignees,
  anchorProps,
  displayHotkey,
  MAX_DISPLAYED_ASSIGNEES = 2,
  readonly,
}: CompressedAssigneeAnchorProps) => {
  return (
    <SharedPicker
      leadingIconElement={assignees.slice(0, MAX_DISPLAYED_ASSIGNEES).map(a => (
        <AssigneeLeadingVisual assignee={a} key={a.login} />
      ))}
      anchorText={assignees.length > 1 ? LABELS.assignees : LABELS.noAssignees}
      anchorProps={anchorProps}
      sharedPickerMainValue={assignees
        .slice(0, MAX_DISPLAYED_ASSIGNEES)
        .map(a => (a.isCopilot ? 'Copilot' : a.login))
        .join(', ')
        .concat(assignees.length > MAX_DISPLAYED_ASSIGNEES ? `, ${assignees.length - MAX_DISPLAYED_ASSIGNEES}+` : '')}
      ariaLabel={LABELS.selectAssignees}
      readonly={readonly}
      leadingIcon={PeopleIcon}
      hotKey={!readonly && displayHotkey ? HOTKEYS.assigneePicker.toUpperCase() : undefined}
      ref={ref}
    />
  )
}

function AssigneeLeadingVisual({assignee}: {assignee: AssigneeObject}) {
  const getHovercardAttributesForType = useGetHovercardAttributesForType()

  if (assignee.isCopilot) {
    return <CopilotIcon />
  } else {
    return (
      <GitHubAvatar
        {...getHovercardAttributesForType('user', {login: assignee.login})}
        src={assignee.avatarUrl}
        size={16}
        alt={`@${assignee.login}`}
        className={styles.compressedAssigneeAvatar}
      />
    )
  }
}

CompressedAssigneeAnchor.displayName = 'CompressedAssigneeAnchor'
